/**
 * Pegel einer Tonspur, fuer die Mikrofon-Anzeigen am Handy und in der Regie.
 *
 * Der Grund fuer die Anzeige ist ein Fehlerbild, das man sonst nicht sieht:
 * ein Handy liefert Bild, aber keinen Ton -- etwa weil iOS das Mikrofon nach
 * einem Anruf oder dem Sperrbildschirm nicht zurueckgegeben hat. Ohne Pegel
 * faellt das erst auf, wenn jemand im Stream zuhoert.
 *
 * Alle Anzeigen teilen sich *einen* Audio-Kontext. Browser begrenzen deren
 * Zahl, und die Regie hat schnell ein Dutzend Vorschauen offen.
 */

let shared: AudioContext | null = null;

function levelContext(): AudioContext | null {
  if (shared) return shared;
  const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  const ctx = new Ctor();
  shared = ctx;
  /*
   * Ein Audio-Kontext laeuft erst nach einer Beruehrung der Seite an. Bis
   * dahin meldet die Messung „unbekannt" statt „still" -- ein stummer Balken
   * hiesse sonst „dein Mikro ist tot", obwohl nur noch niemand getippt hat.
   */
  const wake = () => {
    void ctx.resume().catch(() => undefined);
    if (ctx.state !== 'running') return;
    window.removeEventListener('pointerdown', wake, true);
    window.removeEventListener('keydown', wake, true);
  };
  window.addEventListener('pointerdown', wake, true);
  window.addEventListener('keydown', wake, true);
  void ctx.resume().catch(() => undefined);
  return ctx;
}

/** Wie oft gemessen wird. Zehnmal pro Sekunde reicht fuers Auge. */
const TICK_MS = 100;
/** Alles unter -60 dBFS gilt als Stille, 0 dBFS als voller Ausschlag. */
const FLOOR_DB = 60;

/**
 * Meldet den Pegel eines Streams als 0..1, oder `null`, solange nicht
 * gemessen werden kann. Gibt die Funktion zum Beenden zurueck.
 *
 * Bei einer WebRTC-Gegenstelle liefert Chrome Ton an Web Audio nur, wenn der
 * Stream auch an einem Medienelement haengt -- in der Regie ist das die
 * stumme Vorschau, also gegeben (siehe auch `useMixedAudio`).
 */
export function watchLevel(stream: MediaStream, onLevel: (level: number | null) => void): () => void {
  const ctx = levelContext();
  if (!ctx || stream.getAudioTracks().length === 0) {
    onLevel(null);
    return () => undefined;
  }

  let source: MediaStreamAudioSourceNode;
  try {
    source = ctx.createMediaStreamSource(stream);
  } catch {
    onLevel(null);
    return () => undefined;
  }
  // Nicht mit dem Ausgang verbunden: gemessen wird, abgespielt wird nichts.
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 1024;
  source.connect(analyser);
  const samples = new Float32Array(analyser.fftSize);

  let shown = 0;
  const timer = window.setInterval(() => {
    if (ctx.state !== 'running') return onLevel(null);
    analyser.getFloatTimeDomainData(samples);
    let sum = 0;
    for (const v of samples) sum += v * v;
    const rms = Math.sqrt(sum / samples.length);
    const db = rms > 0 ? 20 * Math.log10(rms) : -Infinity;
    const level = Math.min(1, Math.max(0, (db + FLOOR_DB) / FLOOR_DB));
    // Schnell hoch, langsam runter -- wie ein Pegel am Mischpult.
    shown = level > shown ? level : shown * 0.7 + level * 0.3;
    onLevel(shown);
  }, TICK_MS);

  return () => {
    window.clearInterval(timer);
    source.disconnect();
    analyser.disconnect();
  };
}
